import { parseRange } from '@devicepilot/time';
import { computed, defineComponent, onMounted, ref } from '@vue/composition-api';
import moment from 'moment-timezone';
import { displayDateTime } from '@/helpers/time';
import DpCalendar from './Calendar.vue';
import DpRollingTime from './RollingTime.vue';
import { TimePickerTab, getDayPeriods, getFurtherBackPeriods, getHourPeriods, getRollingPeriodFromMs } from './helpers';
export function getDefaultTimePeriod() {
    const fullTimeRange = {
        anchor: new Date().toISOString(),
        duration: '-1d',
        rolling: true,
    };
    return fullTimeRange;
}
export default defineComponent({
    name: 'DpTimePicker',
    components: {
        DpCalendar,
        DpRollingTime,
    },
    props: {
        defaultText: { type: String, default: 'Default' },
        clearable: { type: Boolean, default: true },
        selectedRolling: { type: Number, default: null },
    },
    setup(props, { emit }) {
        const dropdownVisible = ref(false);
        const hourPeriods = getHourPeriods();
        const dayPeriods = getDayPeriods();
        const furtherBackPeriods = getFurtherBackPeriods();
        const rollingValue = ref(null);
        const periodsValue = ref(null);
        const calendarValue = ref(null);
        const tab = ref(TimePickerTab.Rolling);
        const changeTab = (newTab) => {
            tab.value = newTab;
        };
        // The description in the header bar
        const { defaultText } = props;
        const timeDescription = ref([defaultText]);
        const getTimeDescription = () => {
            const { value: currentTab } = tab;
            const defaultLines = [defaultText];
            // Rolling time tab
            if (currentTab === TimePickerTab.Rolling) {
                const { value } = rollingValue;
                if (!value)
                    return defaultLines;
                const periods = [...hourPeriods, ...dayPeriods, ...furtherBackPeriods];
                const period = periods.find((p) => p.period === value.period);
                return period ? [`Last ${period.label}`] : [];
            }
            // Periods tab
            if (currentTab === TimePickerTab.Periods) {
                const { value } = periodsValue;
                if (!value)
                    return defaultLines;
                return [value.description];
            }
            // Calendar tab
            if (currentTab === TimePickerTab.Calendar) {
                const { value } = calendarValue;
                if (!value)
                    return defaultLines;
                const { start, end } = value;
                const format = 'MMMM Do YYYY';
                return start.isSame(end, 'day')
                    ? [start.format(format)]
                    : [start.format(format), end.format(format)];
            }
            return [defaultText];
        };
        const updateTimeDescription = async () => {
            timeDescription.value = getTimeDescription();
        };
        const timePeriod = ref(null);
        const timeRange = ref(null);
        const updateTimeRange = ((range) => {
            // Update the description only when range is changed
            updateTimeDescription();
            if (!range) {
                timeRange.value = null;
                emit('change', null);
                return;
            }
            const { anchor, inclusive, duration } = range;
            const fullTimeRange = {
                anchor,
                inclusive,
                duration,
            };
            if (range.rolling !== undefined) {
                fullTimeRange.rolling = range.rolling;
            }
            timeRange.value = fullTimeRange;
            emit('change', fullTimeRange);
        });
        const updateFromRolling = ((tp) => {
            // Allow calling this function without a value to update the anchor to now, but
            // keep the duration as the last selected value
            if (tp !== null) {
                rollingValue.value = tp;
            }
            if (rollingValue.value !== null) {
                const updateParams = {
                    anchor: new Date().toISOString(),
                    duration: `-${rollingValue.value.period}`,
                    inclusive: false,
                    rolling: true,
                };
                updateTimeRange(updateParams);
            }
        });
        const updateFromPeriod = ((rollingTime) => {
            if (!rollingTime || !rollingTime.range) {
                periodsValue.value = null;
                updateTimeRange(null);
                return;
            }
            const { period, inclusive } = rollingTime.range;
            periodsValue.value = rollingTime;
            updateTimeRange({
                anchor: new Date().toISOString(),
                duration: `-${period}`,
                inclusive: inclusive || false, // cast undefined to false
            });
        });
        const updateFromCalendar = ((dateRange) => {
            const { start, end } = dateRange;
            calendarValue.value = dateRange;
            const anchor = end.toISOString();
            const duration = `${start.diff(end, 'days') - 1}d`;
            updateTimeRange({
                anchor,
                duration,
                // Calendar ranges will always include the end
                inclusive: true,
            });
        });
        const clear = (() => {
            rollingValue.value = null;
            periodsValue.value = null;
            calendarValue.value = null;
            updateTimeRange(null);
        });
        // The exact start and end dates for the full preview
        const timePreview = computed(() => {
            const { value } = timeRange;
            if (!value)
                return { start: null, end: null };
            // Input spec is slightly different, so this needs some coercion
            const parsed = parseRange({
                ...value,
                anchor: value.anchor,
                duration: value.duration,
            }, {
                clientTimezone: moment.tz.guess(),
            });
            return {
                start: displayDateTime(new Date(parsed.start)),
                end: displayDateTime(new Date(parsed.end)),
            };
        });
        onMounted(() => {
            const { selectedRolling } = props;
            if (selectedRolling) {
                const rolling = getRollingPeriodFromMs(selectedRolling);
                if (rolling) {
                    updateFromRolling(rolling);
                }
            }
        });
        const periodTypes = {
            hourPeriods,
            dayPeriods,
            furtherBackPeriods,
        };
        return {
            dropdownVisible,
            timeRange,
            timePeriod,
            periodTypes,
            rollingValue,
            periodsValue,
            calendarValue,
            updateFromRolling,
            updateFromPeriod,
            updateFromCalendar,
            timePreview,
            timeDescription,
            tab,
            changeTab,
            clear,
        };
    },
});
